import { useMemo, useState } from "react";
import { siteConfig } from "../config/siteConfig";
import Lightbox from "./Lightbox";
import { Expand } from "lucide-react";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "heritage", label: "Anuradhapura" },
  { id: "rooms", label: "Rooms" },
  { id: "restaurant", label: "Restaurant" },
  { id: "bar", label: "Bar" },
  { id: "food", label: "Food" },
  { id: "exterior", label: "Exterior" },
  { id: "reception", label: "Reception" },
];

export default function Gallery() {
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(-1);

  const items = useMemo(() => {
    if (filter === "all") return siteConfig.gallery;
    return siteConfig.gallery.filter((g) => g.category === filter);
  }, [filter]);

  return (
    <section
      id="gallery"
      data-testid="gallery-section"
      className="section bg-[#FAF9F6]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 reveal">
          <p className="font-overline mb-4">Moments</p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#1A362D] mb-4">
            Gallery
          </h2>
          <div className="gold-divider"></div>
          <p className="text-[#2C2C2C] max-w-2xl mx-auto mt-6 text-base sm:text-lg">
            From the ancient stupas of Anuradhapura to our quiet courtyards —
            a visual journey through Thuruliya.
          </p>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mb-10 reveal">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              data-testid={`gallery-filter-${f.id}`}
              className={`px-5 py-2 text-xs uppercase tracking-[0.22em] rounded-full border transition-all duration-300 ${
                filter === f.id
                  ? "bg-[#1A362D] text-[#FAF9F6] border-[#1A362D]"
                  : "bg-transparent text-[#1A362D] border-[#EBE5D9] hover:border-[#C5A059] hover:text-[#C5A059]"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="masonry" data-testid="gallery-masonry">
          {items.map((g, i) => (
            <button
              key={`${g.src}-${i}-${filter}`}
              onClick={() => setOpen(i)}
              className={`masonry-item group block w-full overflow-hidden rounded-3xl bg-[#EBE5D9] relative ${
                g.aspect === "tall" ? "aspect-[3/4]" : "aspect-[4/3]"
              }`}
              data-testid={`gallery-item-${i}`}
              aria-label={`Open ${g.alt}`}
            >
              <img
                src={g.src}
                alt={g.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="absolute inset-0 flex flex-col items-center justify-center text-[#FAF9F6] opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                <Expand className="w-6 h-6 mb-2" strokeWidth={1.5} />
                <span className="text-xs uppercase tracking-[0.22em]">
                  {g.alt}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {open >= 0 && (
        <Lightbox
          items={items}
          index={open}
          onClose={() => setOpen(-1)}
          onChange={(i) => setOpen(i)}
        />
      )}
    </section>
  );
}
